import React from "react";
import { Table, Checkbox, Button, Tag, Card } from "antd";
import { ServiceForInvoice } from "../../types";

interface ServiceTableProps {
  services: ServiceForInvoice[];
  selectedServices: string[];
  toggleServiceSelection: (serviceId: string) => void;
  toggleSupplySelection: (serviceId: string, supplyId: string) => void;
  toggleAdminFeeSelection: (serviceId: string, feeId: string) => void;
  formatServiceForTable: (service: ServiceForInvoice) => {
    description: string;
    amount: number;
  };
}

export const ServiceTable: React.FC<ServiceTableProps> = ({
  services,
  selectedServices,
  toggleServiceSelection,
  toggleSupplySelection,
  toggleAdminFeeSelection,
  formatServiceForTable,
}) => {
  const serviceColumns = [
    {
      title: "Select",
      dataIndex: "id",
      key: "select",
      render: (id: string, record: ServiceForInvoice) => (
        <Checkbox
          checked={selectedServices.includes(id)}
          onChange={() => toggleServiceSelection(id)}
          disabled={record.invoiceStatus !== "not_invoiced"}
        />
      ),
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "invoiceStatus",
      key: "invoiceStatus",
      render: (status: ServiceForInvoice["invoiceStatus"]) => {
        const statusConfig = {
          paid: { color: "green", label: "Paid" },
          invoiced: { color: "orange", label: "Invoiced" },
          not_invoiced: { color: "blue", label: "Not Invoiced" },
        };
        return (
          <Tag color={statusConfig[status].color}>
            {statusConfig[status].label}
          </Tag>
        );
      },
      width: 120,
    },
    {
      title: "Service Ref",
      dataIndex: "serviceReference",
      key: "serviceReference",
    },
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
    },
    {
      title: "Date",
      dataIndex: "serviceDate",
      key: "serviceDate",
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Duration",
      key: "duration",
      render: (_: string, record: ServiceForInvoice) =>
        ["COLLECTION", "DELIVERY", "ON_SITE_SERVICE"].includes(
          record.serviceType
        )
          ? `${record.totalHours.toFixed(2)} hrs`
          : "-",
    },
    {
      title: "Cubic Ft",
      key: "cubicFootage",
      render: (_: string, record: ServiceForInvoice) =>
        ["WAREHOUSE_SERVICE"].includes(record.serviceType)
          ? record.inventoryCubicFootage.toFixed(2)
          : "-",
    },
    {
      title: "Amount",
      key: "amount",
      render: (_: string, record: ServiceForInvoice) => {
        const { amount } = formatServiceForTable(record);
        return `$${amount.toFixed(2)}`;
      },
    },
    {
      title: "Details",
      key: "expand",
      render: () => <Button type="link">View Details</Button>,
    },
  ];

  const expandedRowRender = (record: ServiceForInvoice) => (
    <Card className="bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2">Service Details</h4>
          <p>
            <strong>Vessel:</strong> {record.vesselName} (${record.vesselRate}
            /hr)
          </p>
          <p>
            <strong>Staff:</strong> {record.staffNames.join(", ")}
          </p>
          <p>
            <strong>Location:</strong> {record.location}
          </p>
          {record.travelTime > 0 && (
            <p>
              <strong>Travel Time:</strong> {record.travelTime} hours
            </p>
          )}
          <p>
            <strong>Minimum Hours:</strong> {record.minimumHours}
          </p>
        </div>
        <div>
          <h4 className="font-medium mb-2">Billable Items</h4>
          {record.suppliesUsed.length > 0 && (
            <div className="mb-3">
              <h5 className="font-medium">Supplies/Materials:</h5>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left">Include</th>
                    <th className="text-left">Item</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {record.suppliesUsed.map((supply) => (
                    <tr key={supply.id} className="border-b">
                      <td>
                        <Checkbox
                          checked={supply.selected}
                          onChange={() =>
                            toggleSupplySelection(record.id, supply.id)
                          }
                        />
                      </td>
                      <td>{supply.name}</td>
                      <td className="text-right">{supply.quantity}</td>
                      <td className="text-right">${supply.price.toFixed(2)}</td>
                      <td className="text-right">
                        ${(supply.price * supply.quantity).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {record.adminFees.length > 0 && (
            <div>
              <h5 className="font-medium">Admin Fees:</h5>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left">Include</th>
                    <th className="text-left">Fee</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {record.adminFees.map((fee) => (
                    <tr key={fee.id} className="border-b">
                      <td>
                        <Checkbox
                          checked={fee.selected}
                          onChange={() =>
                            toggleAdminFeeSelection(record.id, fee.id)
                          }
                        />
                      </td>
                      <td>{fee.name}</td>
                      <td className="text-right">${fee.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <Table
      dataSource={services.filter((s) => s.serviceType !== "STORAGE")}
      columns={serviceColumns}
      rowKey="id"
      expandable={{
        expandedRowRender,
        expandRowByClick: true,
      }}
      pagination={false}
    />
  );
};
